import React, { useState, useRef } from 'react'
import styled from '@emotion/styled'

import { PillButton } from '../../generic/button'
import { IconGroup } from '../../generic/buttonGroup'

import { FlexRowDiv } from '../../generic/Basics'
import { blueBase, redLight10, redLight45, fontSize3XSmall } from '../../../constants/principles'
import ExclamationTriangle from '../../generic/ExclamationTriangle'
import Spinner from '../../generic/spinner'
import { useGlobalState } from '../../../hooks/GlobalStateProvider'

import { WRONG_IMAGE_TYPE_ERROR, FAILED_TO_DELETE_ERROR } from '../../../constants/errors'

export default ({ id, name, category, data, svg, showPhotoGuidelinesModal, uploadSelectedImage, deleteSelectedImage, testId }) => {
  const { globalState } = useGlobalState()
  const { imageData, error, loading } = data
  const [isActive, setIsActive] = useState(false)
  const uploaded = !!imageData
  const SVG = svg
  const inputElement = useRef()
  const clickHiddenInput = () => {
    if (inputElement && inputElement.current) {
      inputElement.current.click()
    }
  }
  const onImageUploadSelected = e => {
    const imageFile = e.target.files[0]
    const fileName = typeof e.target.value === 'string' ? e.target.value.replace(/^.*[\\/]/, '') : ''
    if (imageFile && fileName) {
      uploadSelectedImage({
        category,
        fileName,
        imageFile
      })
    }
  }

  return (
    <IconGroup>
      <HiddenInput
        type="file"
        ref={inputElement}
        accept="image/*"
        data-testid={`${testId}-input-${name
          ?.toString()
          .replace(/[^A-Z0-9]/gi, '-')
          .toLowerCase()}`}
        onChange={onImageUploadSelected}
        onClick={e => {
          e.target.value = null
        }}
      />
      {loading ? (
        <StyledWidget>
          <PaddingDiv>
            <Spinner absolute={false} size={'32px'} />
          </PaddingDiv>
        </StyledWidget>
      ) : !uploaded && !error ? (
        <StyledWidget
          id={id}
          onClick={clickHiddenInput}
          onMouseDown={() => setIsActive(true)}
          onMouseOver={() => {
            setIsActive(false)
          }}
          onMouseUp={() => setIsActive(false)}
          onMouseOut={() => {
            setIsActive(false)
          }}>
          <FullWidthFlex>{name}</FullWidthFlex>
          {SVG && (
            <FullWidthFlex>
              <SVG color={isActive ? 'white' : 'black'} width={80} height={80} id={name} alt="icon" />
            </FullWidthFlex>
          )}
        </StyledWidget>
      ) : uploaded && !error ? (
        <SuccessWidget id={id}>
          <StyledI
            data-testid={`${testId}-delete-${name
              ?.toString()
              .replace(/[^A-Z0-9]/gi, '-')
              .toLowerCase()}`}
            iconSize={140}
            onClick={() => deleteSelectedImage({ imageData, category, categoryName: name })}>
            &#xf1f8;
          </StyledI>
          <FullWidthFlex>
            <StyledImg alt="check" src={__PATH_PREFIX__ + '/checkmark.svg'} />
          </FullWidthFlex>
          <FullWidthFlex>{name}</FullWidthFlex>
        </SuccessWidget>
      ) : (
        error && (
          <ErrorWidget id={id}>
            <FullWidthFlex>
              <ExclamationTriangle colorFill={redLight10} width={18} height={18}></ExclamationTriangle>
            </FullWidthFlex>
            <FullWidthFlex>{name}</FullWidthFlex>
            <FullWidthFlex
              data-testid={`${testId}-error-${name
                ?.toString()
                .replace(/[^A-Z0-9]/gi, '-')
                .toLowerCase()}`}
              weight={'bold'}>
              {error === FAILED_TO_DELETE_ERROR ? `Couldn't Delete` : `Failed To Upload`}
            </FullWidthFlex>
            {error === WRONG_IMAGE_TYPE_ERROR && <FakeButton onClick={showPhotoGuidelinesModal}>See Guidelines</FakeButton>}
            <FakeButton onClick={() => (error === FAILED_TO_DELETE_ERROR ? deleteSelectedImage({ imageData, category, categoryName: name }) : clickHiddenInput())}>Try Again</FakeButton>
          </ErrorWidget>
        )
      )}
    </IconGroup>
  )
}

const StyledImg = styled.img`
  width: 32px;
  height: 32px;
`
const FullWidthFlex = styled(FlexRowDiv)`
  padding-top: 4px;
  width: 100%;
  font-size: ${fontSize3XSmall};
  font-weight: ${p => (p.weight ? p.weight : 'normal')};
  word-break: break-word;
  margin: 0 4px;
`
const StyledWidget = styled(PillButton)`
  color: initial;
  display: flex;
  flex-wrap: wrap;
  height: 128px;
  justify-content: center;
  padding: 10px 2px;
  width: 136px;
`
const SuccessWidget = styled(StyledWidget)`
  background-color: ${blueBase};
  align-content: space-between;
  padding: 10px 2px 16px 2px;
  color: white;
  &:focus {
    background-color: ${blueBase};
    color: white;
  }
  &:hover {
    background-color: ${blueBase};
    color: white;
  }
  &:active {
    background-color: ${blueBase};
    color: white;
  }
`
const ErrorWidget = styled(StyledWidget)`
  background-color: ${redLight45};
  color: ${redLight10};
  &:focus {
    background-color: ${redLight45};
    color: ${redLight10};
  }
  &:hover {
    background-color: ${redLight45};
    color: ${redLight10};
  }
  &:active {
    background-color: ${redLight45};
    color: ${redLight10};
  }
`
const FakeButton = styled.div`
  color: ${blueBase};
  cursor: pointer;
  font-size: ${fontSize3XSmall};
  font-weight: bold;
`
const HiddenInput = styled.input`
  opacity: 0;
  height: 0;
  display: none !important;
`
const StyledI = styled.i`
  font-family: 'fontello';
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  font-size: ${p => p.iconSize}%;
  margin-left: auto;
  margin-right: 6px;
`
const PaddingDiv = styled.div`
  padding-top: 40px;
`
